import React, { createContext, FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { slugs } from '@/constants'

interface TexasModalContextType {
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
}

export const TexasModalContext = createContext<TexasModalContextType>({
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
})

interface TexasModalProviderProps {
  children: ReactNode
  theatricalSlug: string
  shouldOpenModal?: boolean
  initialIsOpen?: boolean
}

export const TexasModalProvider: FC<TexasModalProviderProps> = ({
  children,
  theatricalSlug,
  shouldOpenModal = false,
  initialIsOpen = false,
}) => {
  const isTheLastRodeo = theatricalSlug === slugs.theLastRodeo
  const modalStateRef = useRef(initialIsOpen)
  const [isModalOpen, setIsModalOpen] = useState(initialIsOpen)

  useEffect(() => {
    modalStateRef.current = isModalOpen
  }, [isModalOpen])

  const hasSeenPromoRef = useRef(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      hasSeenPromoRef.current = localStorage.getItem('has-seen-premiere-promo') === 'true'
    }
  }, [])

  useEffect(() => {
    if (shouldOpenModal && isTheLastRodeo && !modalStateRef.current && !hasSeenPromoRef.current) {
      setIsModalOpen(true)
    }
  }, [shouldOpenModal, isTheLastRodeo])

  const openModal = useCallback(() => {
    if (isTheLastRodeo) {
      setIsModalOpen(true)
    }
  }, [isTheLastRodeo])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    if (typeof window !== 'undefined' && isTheLastRodeo) {
      localStorage.setItem('has-seen-premiere-promo', 'true')
      hasSeenPromoRef.current = true
    }
  }, [isTheLastRodeo])

  return (
    <TexasModalContext.Provider
      value={{
        isModalOpen,
        openModal,
        closeModal,
      }}
    >
      {children}
    </TexasModalContext.Provider>
  )
}
